import type {
  OptimizerAuditRollup,
  OptimizerAuditV2DecisionImpact,
  OptimizerAuditV2Record,
} from '../../types/audit.types';

export type {
  OptimizerAuditRollup,
  OptimizerAuditV2DecisionImpact,
  OptimizerAuditV2Record,
} from '../../types/audit.types';

const MATERIAL_IMPACTS = new Set<OptimizerAuditV2DecisionImpact>([
  'MATERIAL',
  'BLOCKING',
]);

export const aggregateOptimizerAudits = (
  audits: OptimizerAuditV2Record[],
): OptimizerAuditRollup => {
  let passedCount = 0;
  let failedValidationCount = 0;
  let totalTradeCount = 0;
  let totalBuyCents = 0;
  let totalSellCents = 0;
  let materialDecisionCount = 0;
  let durationTotalMs = 0;
  let durationCount = 0;
  const seenReportIds = new Set<string>();
  const uniqueAudits: OptimizerAuditV2Record[] = [];

  for (const audit of audits) {
    const reportId = audit.portfolioReportId;

    if (reportId) {
      if (seenReportIds.has(reportId)) {
        continue;
      }

      seenReportIds.add(reportId);
    }

    uniqueAudits.push(audit);
  }

  for (const audit of uniqueAudits) {
    if (audit.passed === true) {
      passedCount += 1;
    }

    if (audit.passed === false) {
      failedValidationCount += 1;
    }

    for (const subAccount of audit.subAccountResults ?? []) {
      totalTradeCount += subAccount.tradeCount ?? 0;
      totalBuyCents += subAccount.totalBuyCents ?? 0;
      totalSellCents += subAccount.totalSellCents ?? 0;
    }

    for (const decision of audit.decisions ?? []) {
      if (decision.impact && MATERIAL_IMPACTS.has(decision.impact)) {
        materialDecisionCount += 1;
      }
    }

    const durationMs = audit.durationMs ?? audit.solver?.durationMs;
    if (durationMs != null && durationMs > 0) {
      durationTotalMs += durationMs;
      durationCount += 1;
    }
  }

  return {
    portfolioCount: uniqueAudits.length,
    passedCount,
    failedValidationCount,
    totalTradeCount,
    totalBuyCents,
    totalSellCents,
    materialDecisionCount,
    avgDurationMs:
      durationCount > 0 ? Math.round(durationTotalMs / durationCount) : null,
  };
};

export const collectUniqueValidationErrors = (
  audits: OptimizerAuditV2Record[],
): string[] => {
  const errors = new Set<string>();

  for (const audit of audits) {
    for (const error of audit.validationErrors ?? []) {
      if (error) {
        errors.add(error);
      }
    }
  }

  return [...errors];
};
